"use client";

import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useState, type FormEvent } from "react";
import { useLogin } from "@/app/_hooks/Auth/useLogin";
import { useLoginError } from "@/app/_hooks/Auth/useLoginError";

export default function LoginForm() {
  const t = useTranslations("auth");
  const router = useRouter();
  const locale = useLocale();
  const { loginWithAuth0, isLoading, error } = useLogin();
  const { message: loginError, forceLogin } = useLoginError();
  const [email, setEmail] = useState("");
  const alertMessage = loginError ?? error;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isLoading) return;
    loginWithAuth0(email, {
      forceLogin,
    });
  };

  return (
    <Box className="w-full">
      <Typography variant="h4" fontWeight={700} gutterBottom>
        {t("loginTitle")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t("loginSubtitle")}
      </Typography>

      {alertMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {alertMessage}
        </Alert>
      )}

      <Box
        className="flex flex-col gap-3"
        component="form"
        onSubmit={handleSubmit}
      >
        <TextField
          label={t("emailLabel")}
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          fullWidth
          disabled={isLoading}
        />
        <LoadingButton
          variant="contained"
          size="large"
          type="submit"
          loading={isLoading}
          disabled={isLoading}
        >
          {t("loginAction")}
        </LoadingButton>
        {forceLogin && (
          <Button
            variant="text"
            size="small"
            type="button"
            onClick={() => {
              if (typeof window === "undefined") return;
              window.location.assign(
                `/api/auth/logout?returnTo=/${locale}/login`
              );
            }}
            disabled={isLoading}
          >
            {t("loginSwitchAccount")}
          </Button>
        )}
        <Button
          variant="text"
          size="small"
          type="button"
          onClick={() => router.push(`/${locale}/forgot-password`)}
          sx={{ alignSelf: "flex-start" }}
        >
          {t("forgotPassword")}
        </Button>
      </Box>
    </Box>
  );
}
